import React, { useEffect, useRef, useState } from "react";
import useDocumentByAdminQuery from "features/document/hooks/queries/detail/useDocumentByAdminQuery";
import useDocumentDetailQuery from "features/document/hooks/queries/detail/useDocumentDetailQuery";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import DocumentDetail from "components/document/read/common/DocumentDetail";
import { DOCUMENT_UUID } from "config/constant";
import LeaveApplicationDetail from "components/document/read/work/LeaveApplicationDetail";
import ReceiveOfficialLetterDetail from "components/document/read/work/ReceiveOfficialLetterDetail";
import SendOfficialLetterDetail from "components/document/read/work/SendOfficialLetterDetail";
import ExpenseReportDetail from "components/document/read/work/ExpenseReportDetail";
import MinutesOfMeetingDetail from "components/document/read/work/MinutesOfMeetingDetail";
import KSSDetail from "components/document/read/work/KSSDetail";
import ReportDetail from "components/document/read/work/ReportDetail";
import LetterOfApprovalDetail from "components/document/read/work/LetterOfApprovalDetail";
import CorpCardUsageProofDetail from "components/document/read/work/CorpCardUsageProofDetail";
import { FormProvider, useForm } from "react-hook-form";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import { DOCUMENT_DETAIL_FIELD as FIELD } from "config/documentConstant";
import DocumentDetailFooterForDecisionManager from "components/document/read/common/footer/DocumentDetailFooterForDecisionManager";
import {
  DocumentDetailResponse,
  DocumentDto,
  UserDetailResponse,
} from "apis/data-contracts";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import DocumentViewer from "components/document/read/DocumentViewer";
import { SignInfo } from "components/document/read/common/DocumentDetailSignSection";
import { pdf } from "@react-pdf/renderer";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import usePDF from "features/pdf/hooks/usePDF";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentDetailFooterForm {
  [FIELD.COMMENT.key]: string;
  [FIELD.REFERENCE_USERS.key]: string[];
}

const Doc = ({
  adminDocResponse,
  currentUser,
  approvedUserInfo,
  showApproverSign,
  approveComment,
}: {
  adminDocResponse: DocumentDto;
  currentUser?: UserDetailResponse;
  approvedUserInfo?: SignInfo;
  showApproverSign?: boolean;
  approveComment?: string;
}) => {
  return (
    <DocumentViewer
      formComponent={<Form adminDocumentDetail={adminDocResponse} />}
      companyName={adminDocResponse.companyName}
      documentClassName={adminDocResponse.documentClassDto.name}
      documentApproveStatus={
        adminDocResponse.documentApprove as DocumentDetailResponse["approveStatus"]
      }
      tags={adminDocResponse.tagList}
      createdUserInfo={{
        name: adminDocResponse.author,
        signImageUrl: adminDocResponse.authorSignImageUrl,
        date: adminDocResponse.createdAt,
      }}
      showApproverSign={showApproverSign}
      approvedUserInfo={
        showApproverSign
          ? currentUser && {
              name: currentUser.name,
              signImageUrl: currentUser.signImageUrl,
              date: new Date().toISOString(),
            }
          : approvedUserInfo
      }
      attachmentList={adminDocResponse.attachments}
      approveComment={
        approveComment ||
        adminDocResponse?.additionalFields?.approveComment ||
        ""
      }
    />
  );
};

const WorkDetailPageForDecisionManager = () => {
  const documentUuid = useParams().uuid as string;

  const { data: adminDocResponse } = useDocumentByAdminQuery(documentUuid);
  const { data: documentData } = useDocumentDetailQuery(documentUuid);
  const approvedUser = documentData?.approvedUserDto?.uuid;
  const approvedUserName = documentData?.approvedUserDto?.name;
  const approvedUserSignImageUrl = documentData?.approvedUserDto?.signImageUrl;
  const approvedAt = documentData?.approveStatusUpdatedAt;
  const approvedUserInfo =
    approvedUserName && approvedAt
      ? {
          name: approvedUserName,
          signImageUrl: approvedUserSignImageUrl,
          date: approvedAt,
        }
      : undefined;

  /* 결재권자 정보 */
  const { data: currrentUser } = useUserDetailQuery();

  /* 폼 */
  const methods = useForm<DocumentDetailFooterForm>();

  const { uploadDocPdfAndGetS3Url } = usePDF<HTMLDivElement>();

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const pdfDevicePixelRatio =
    typeof window !== "undefined"
      ? Math.max(window.devicePixelRatio || 1, 2)
      : 2;

  const handleUploadPdf = async (fileName: string, approveComment?: string) => {
    const basePdfBlob = await pdf(
      <Doc
        adminDocResponse={adminDocResponse as DocumentDto}
        currentUser={currrentUser}
        approvedUserInfo={approvedUserInfo}
        showApproverSign={true}
        approveComment={approveComment}
      />,
    ).toBlob();

    return await uploadDocPdfAndGetS3Url(basePdfBlob)(fileName);
  };

  /* BeforeUnload 비활성화 */
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  /** 화면 렌더용 blob url */
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const prevBlobUrlRef = useRef<string | null>(null);

  /** 페이지 수 */
  const [numPages, setNumPages] = useState<number>(0);
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  /** PC에서 width 맞춰 렌더링 */
  const pcPdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [pcPdfWidth, setPcPdfWidth] = useState<number>(0);

  useEffect(() => {
    if (isMobile) return;

    const el = pcPdfContainerRef.current;
    if (!el) return;

    const update = () => setPcPdfWidth(Math.max(0, el.clientWidth - 16));
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [isMobile]);

  /**
   * adminDocResponse/currentUser 기반으로 PDF blob 생성
   * - mobile/pc 공통으로 생성해서 react-pdf로 “본문만” 렌더
   */
  useEffect(() => {
    if (!adminDocResponse) return;

    let cancelled = false;
    const stableApprovedUserInfo =
      approvedUserName && approvedAt
        ? {
            name: approvedUserName,
            signImageUrl: approvedUserSignImageUrl,
            date: approvedAt,
          }
        : undefined;

    (async () => {
      const basePdfBlob = await pdf(
        <Doc
          adminDocResponse={adminDocResponse as DocumentDto}
          currentUser={currrentUser}
          approvedUserInfo={stableApprovedUserInfo}
        />,
      ).toBlob();

      if (cancelled) return;

      const url = URL.createObjectURL(basePdfBlob);

      // 이전 blob revoke (메모리 누수 방지)
      const prev = prevBlobUrlRef.current;
      prevBlobUrlRef.current = url;
      if (prev) setTimeout(() => URL.revokeObjectURL(prev), 1000);

      setPdfUrl(url);
      setNumPages(0);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    adminDocResponse,
    approvedAt,
    approvedUserName,
    approvedUserSignImageUrl,
    currrentUser,
  ]);

  // unmount 시 마지막 revoke
  useEffect(() => {
    return () => {
      const last = prevBlobUrlRef.current;
      if (last) URL.revokeObjectURL(last);
    };
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <WrapStyle>
          {adminDocResponse && (
            <>
              {isMobile ? (
                pdfUrl ? (
                  <Document
                    key={pdfUrl}
                    file={pdfUrl}
                    loading={null}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) =>
                      console.error("PDF 로딩 오류", error)
                    }
                  >
                    {Array.from(new Array(numPages), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        scale={2.0}
                        devicePixelRatio={pdfDevicePixelRatio}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    ))}
                  </Document>
                ) : (
                  <PdfPlaceholder />
                )
              ) : (
                <PdfLayoutStyle ref={pcPdfContainerRef}>
                  {pdfUrl ? (
                    <Document
                      key={pdfUrl}
                      file={pdfUrl}
                      loading={null}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) =>
                        console.error("PDF 로딩 오류", error)
                      }
                    >
                      {Array.from(new Array(numPages), (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={pcPdfWidth || undefined}
                          devicePixelRatio={pdfDevicePixelRatio}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      ))}
                    </Document>
                  ) : (
                    <PdfPlaceholder />
                  )}
                </PdfLayoutStyle>
              )}

              {adminDocResponse.attachments.length > 0 && (
                <DocumentDetail.AttachmentList
                  attachmentList={adminDocResponse.attachments}
                />
              )}
            </>
          )}

          <DocumentDetailFooterForDecisionManager
            documentUuid={documentUuid}
            setBeforeUnloadDisable={setBeforeUnloadDisable}
            uploadDocPdfAndGetS3Url={handleUploadPdf}
            referenceUsers={adminDocResponse?.referenceUsers}
            permissionType={adminDocResponse?.permissionType}
            documentApprove={adminDocResponse?.documentApprove || ""}
            author={adminDocResponse?.authorUuid || ""}
            comment={adminDocResponse?.comment || ""}
            approvedUser={approvedUser}
          />
        </WrapStyle>
      </FormProvider>

      <BeforeUnloadModal
        isDirty={methods.formState.isDirty}
        disable={beforeUnloadDisable}
      />
    </>
  );
};

export default WorkDetailPageForDecisionManager;

interface FormProps {
  adminDocumentDetail: DocumentDto;
}

const Form = ({ adminDocumentDetail }: FormProps) => {
  const documentClassUuid = adminDocumentDetail.documentClassDto.uuid;
  const documentData = adminDocumentDetail.documentData;

  switch (documentClassUuid) {
    case DOCUMENT_UUID.WORK.classes.휴가신청서.uuid:
      return <LeaveApplicationDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["공문 수신"].uuid:
      return <ReceiveOfficialLetterDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["공문 발신"].uuid:
      return <SendOfficialLetterDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.품의서.uuid:
      return <LetterOfApprovalDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.지출결의서.uuid:
      return <ExpenseReportDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.회의록.uuid:
      return <MinutesOfMeetingDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.보고서.uuid:
      return <ReportDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.KSS.uuid:
      return <KSSDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["법인카드 사용 증빙"].uuid:
      return (
        <CorpCardUsageProofDetail
          documentData={documentData}
          companyName={adminDocumentDetail.companyName}
          author={adminDocumentDetail.author}
        />
      );
  }

  return null;
};

/* 스타일 */
const WrapStyle = styled.div`
  width: 100.5rem;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 2rem;

  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.media.small} {
    .react-pdf__Document,
    .react-pdf__Page {
      width: 100% !important;
    }

    .react-pdf__Page__canvas {
      width: 100% !important;
      height: auto !important;
      display: block;
    }

    background-color: ${({ theme }) => theme.color.background.white};
    width: 100%;
  }
`;

const PdfLayoutStyle = styled.div`
  max-width: 100.5rem;
  width: 100%;
  margin: 0 auto;

  .react-pdf__Document {
    width: 100%;
  }

  .react-pdf__Page {
    width: 100% !important;
    display: flex;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .react-pdf__Page__canvas {
    width: 100% !important;
    height: auto !important;
    display: block;
  }
`;

const PdfPlaceholder = styled.div`
  width: 100%;
  min-height: 32rem;

  ${({ theme }) => theme.media.small} {
    min-height: 40vh;
  }
`;
